import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import {
  OUTCOME_LIST,
  hasOutcome,
  type ScriptAction,
  type ScriptCell,
  type ScriptOutcome,
} from '../dev/battleScript';

// Icon texture per action, plus the charge (buff) icon. Built in main.ts from
// loaded ui + skill assets and threaded down to the modal/palette/cell.
export type ScriptIcons = Partial<Record<ScriptAction | 'charge', Texture>>;

// Outcome badge colors - reused from the battle log palette (BattleRenderer)
// so the builder reads the same as the live fight.
export const OUTCOME_COLOR: Record<ScriptOutcome, number> = {
  hit: 0xff5555,
  dodge: 0xfbbf24,
  dodgeCounter: 0xff8800,
  dodgeBuffCounter: 0xffd000,
  block: 0xfbbf24,
  pierce: 0xff5555,
};

const OUTCOME_LABEL = new Map(OUTCOME_LIST.map((o) => [o.outcome, o.label]));

const BG_COLOR = 0xe8d5b5;
const BG_ALPHA = 0.55;
const BORDER_COLOR = 0x6d5535;
const TEXT_COLOR = 0x4a3826;

export type ScriptCellViewOptions = {
  cell: ScriptCell;
  icons: ScriptIcons;
  size: number;
  fontFamily?: string;
  textResolution: number;
  onTap: () => void;
};

// One round cell in the builder grid: action icon + outcome badge + small
// buff / counter-kick markers. Tap opens the palette bound to this cell.
export class ScriptCellView extends Container {
  constructor(opts: ScriptCellViewOptions) {
    super();
    const { cell, icons, size, fontFamily, textResolution } = opts;

    const bg = new Graphics()
      .roundRect(0, 0, size, size, size * 0.12)
      .fill({ color: BG_COLOR, alpha: BG_ALPHA })
      .stroke({ color: BORDER_COLOR, width: 1.5 });
    this.addChild(bg);

    const icon = icons[cell.action];
    if (icon) {
      const sprite = new Sprite(icon);
      const target = size * 0.6;
      sprite.anchor.set(0.5);
      sprite.scale.set(target / Math.max(icon.width, icon.height));
      sprite.position.set(size / 2, size * 0.42);
      this.addChild(sprite);
    }

    // Outcome badge (attacks only; repair shows a dim dash).
    const badgeText = hasOutcome(cell.action) ? (OUTCOME_LABEL.get(cell.outcome) ?? '') : '--';
    const badge = new Text({
      text: badgeText,
      style: {
        fontFamily,
        fontSize: size * 0.16,
        fontWeight: 'bold',
        fill: hasOutcome(cell.action) ? OUTCOME_COLOR[cell.outcome] : TEXT_COLOR,
      },
      resolution: textResolution,
    });
    badge.anchor.set(0.5);
    badge.position.set(size / 2, size * 0.85);
    this.addChild(badge);

    // Buff marker (charge icon top-left).
    if (cell.buff && icons.charge) {
      const buff = new Sprite(icons.charge);
      const t = size * 0.26;
      buff.anchor.set(0.5);
      buff.scale.set(t / Math.max(icons.charge.width, icons.charge.height));
      buff.position.set(size * 0.2, size * 0.18);
      this.addChild(buff);
    }

    // Counter-kick marker (small K, top-right).
    if (cell.counterKick) {
      const k = new Text({
        text: 'K',
        style: { fontFamily, fontSize: size * 0.16, fontWeight: 'bold', fill: 0xff8800 },
        resolution: textResolution,
      });
      k.anchor.set(0.5);
      k.position.set(size * 0.82, size * 0.18);
      this.addChild(k);
    }

    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', opts.onTap);
  }
}
